import React, { useState, useEffect, useCallback } from 'react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Plus, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { customFieldsAPI, taxonomyAPI } from '../../services/api';

// Renders the Super-Admin-attached taxonomy fields for an entity (schema-level).
// Values shape: { [taxonomy_id]: string (single) | string[] (multi) }.
export const EntityCustomFields = ({
  entityType, values = {}, onChange, isSuperAdmin = false, currentUser, onSchemaChanged, testPrefix = 'custom-field',
}) => {
  const [fields, setFields] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [addTaxId, setAddTaxId] = useState('');
  const [addMulti, setAddMulti] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadFields = useCallback(async () => {
    if (!entityType) return;
    try {
      const data = await customFieldsAPI.list(entityType);
      setFields(data);
    } catch { /* silent */ }
  }, [entityType]);

  useEffect(() => { loadFields(); }, [loadFields]);
  useEffect(() => {
    if (isSuperAdmin) {
      taxonomyAPI.getAll().then(cats => setTaxonomies(cats.filter(c => !c.is_core))).catch(() => {});
    }
  }, [isSuperAdmin]);

  const attachedIds = fields.map(f => f.taxonomy_id);
  const addable = taxonomies.filter(t => !attachedIds.includes(t.id));

  const handleAttach = async () => {
    if (!addTaxId) { toast.error('Pick a taxonomy'); return; }
    setBusy(true);
    try {
      await customFieldsAPI.attach(entityType, addTaxId, addMulti, currentUser?.email || currentUser?.name || null);
      toast.success('Field added to this entity');
      setAddTaxId(''); setAddMulti(false); setAddOpen(false);
      await loadFields();
      onSchemaChanged && onSchemaChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add field');
    } finally { setBusy(false); }
  };

  const handleDetach = async (field) => {
    if (!window.confirm(`Remove "${field.label}" from every ${entityType.replace(/_/g, ' ')} record? Existing values stay in the database but the field will no longer show.`)) return;
    try {
      await customFieldsAPI.detach(field.id);
      toast.success('Field removed');
      await loadFields();
      onSchemaChanged && onSchemaChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove field');
    }
  };

  const setSingle = (taxId, v) => onChange({ ...values, [taxId]: v });
  const toggleMulti = (taxId, optValue) => {
    const cur = Array.isArray(values[taxId]) ? values[taxId] : (values[taxId] ? [values[taxId]] : []);
    const next = cur.includes(optValue) ? cur.filter(x => x !== optValue) : [...cur, optValue];
    onChange({ ...values, [taxId]: next });
  };

  if (fields.length === 0 && !isSuperAdmin) return null;

  return (
    <div className="space-y-4" data-testid={`${testPrefix}-section`}>
      {fields.map(field => {
        const opts = field.options || [];
        const val = values[field.taxonomy_id];
        return (
          <div key={field.id} className="space-y-1.5" data-testid={`${testPrefix}-${field.taxonomy_id}`}>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5">
                {field.label}
                {field.multi_select && <Badge variant="outline" className="text-[9px] py-0">multi</Badge>}
              </Label>
              {isSuperAdmin && (
                <button type="button" onClick={() => handleDetach(field)}
                  className="text-[11px] text-muted-foreground hover:text-red-600"
                  data-testid={`${testPrefix}-detach-${field.taxonomy_id}`}>
                  Remove field
                </button>
              )}
            </div>
            {field.multi_select ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal" data-testid={`${testPrefix}-multi-trigger-${field.taxonomy_id}`}>
                    <span className="flex flex-wrap gap-1 items-center">
                      {Array.isArray(val) && val.length > 0
                        ? val.map(v => <Badge key={v} variant="secondary" className="text-xs">{opts.find(o => o.value === v)?.label || v}</Badge>)
                        : <span className="text-muted-foreground">Select...</span>}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="start">
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {opts.length === 0 && <p className="text-xs text-muted-foreground px-1">No options in this taxonomy.</p>}
                    {opts.map(o => {
                      const checked = Array.isArray(val) ? val.includes(o.value) : val === o.value;
                      return (
                        <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-1 rounded hover:bg-muted" data-testid={`${testPrefix}-multi-opt-${field.taxonomy_id}-${o.value}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleMulti(field.taxonomy_id, o.value)} />
                          {o.label}
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="flex items-center gap-2">
                <Select value={val || ''} onValueChange={(v) => setSingle(field.taxonomy_id, v)}>
                  <SelectTrigger data-testid={`${testPrefix}-select-${field.taxonomy_id}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
                {val && (
                  <button type="button" onClick={() => setSingle(field.taxonomy_id, '')} className="text-muted-foreground hover:text-foreground" title="Clear" data-testid={`${testPrefix}-clear-${field.taxonomy_id}`}>
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {isSuperAdmin && (
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="border-dashed text-muted-foreground w-full" data-testid={`${testPrefix}-add-btn`}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />Add Taxonomy Field
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 space-y-3" align="start">
            <p className="text-xs font-medium text-muted-foreground">Attach a taxonomy as a permanent field on this entity (Super Admin, one-time).</p>
            <Select value={addTaxId} onValueChange={setAddTaxId}>
              <SelectTrigger data-testid={`${testPrefix}-add-taxonomy-select`}><SelectValue placeholder="Choose taxonomy..." /></SelectTrigger>
              <SelectContent>
                {addable.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">All custom taxonomies already added</div>}
                {addable.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={addMulti} onCheckedChange={(c) => setAddMulti(!!c)} data-testid={`${testPrefix}-add-multi-checkbox`} />
              Allow selecting multiple values
            </label>
            <Button size="sm" className="w-full bg-lime text-lime-foreground hover:bg-lime/90" onClick={handleAttach} disabled={busy || !addTaxId} data-testid={`${testPrefix}-add-confirm-btn`}>
              {busy ? 'Adding...' : 'Add Field'}
            </Button>
            <p className="text-[11px] text-muted-foreground">Need a new list? Create it first in Taxonomy settings, then attach it here.</p>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

// Helper: render a custom field value (single string or array) for tables.
// Pass the field's `options` to map stored values to their display labels.
export const renderCustomValue = (value, options = null) => {
  if (value == null || value === '') return null;
  const toLabel = (v) => {
    if (!options) return v;
    const opt = options.find(o => o.value === v);
    return opt ? opt.label : v;
  };
  if (Array.isArray(value)) return value.length ? value.map(toLabel).join(', ') : null;
  return toLabel(value);
};

export default EntityCustomFields;
